/**
 * Paleta de comenzi (`Ctrl+K`), scrisa de mana: dialog modal cu capcana de focus corecta, rolurile
 * `dialog` / `combobox` / `listbox` / `option` si `aria-activedescendant`.
 *
 * Fara librarie de componente — un dialog cu o lista filtrata e ~150 de linii, iar dependenta ar
 * aduce propriile decizii de stilizare peste sistemul nostru de tokeni.
 *
 * Cautarea pliaza diacriticele (`Intl.Collator` via `foldRo`), deci „plati” gaseste „plati", iar
 * termenii din Excel („scadente", „week", „edl") gasesc ecranul corespunzator.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { foldRo } from '@evomag/core/format';
import { NAVIGATIE, type IntrareNav } from './navigatie.ts';

export interface Comanda {
  id: string;
  eticheta: string;
  grup: string;
  /** Cuvinte suplimentare pentru potrivire (inclusiv terminologia din fisierul Excel). */
  cuvinte: string;
  /** Descriere scurta la dreapta; de obicei intrebarea la care raspunde ecranul. */
  detaliu?: string;
  executa: () => void;
}

export interface PropsCommandPalette {
  deschisa: boolean;
  /** Ziua de context, pentru comenzile care duc la o ruta cu data. */
  ziuaContext: string;
  /** Perioada `2026-W34`, pentru raportul saptamanal. */
  perioada: string;
  /** Comenzi care nu sunt navigare (tema, densitate, export). */
  comenziExtra?: readonly Comanda[];
  onInchide: () => void;
}

export function CommandPalette({
  deschisa,
  ziuaContext,
  perioada,
  comenziExtra = [],
  onInchide,
}: PropsCommandPalette) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [selectat, setSelectat] = useState(0);
  const refInput = useRef<HTMLInputElement>(null);
  const refDialog = useRef<HTMLDivElement>(null);
  const refAnterior = useRef<HTMLElement | null>(null);

  const comenzi = useMemo<readonly Comanda[]>(() => {
    const navigare = NAVIGATIE.map((n) => ({
      id: `nav-${n.cheie}`,
      eticheta: n.eticheta,
      grup: 'Ecrane',
      cuvinte: n.cuvinte,
      detaliu: n.intrebare,
      executa: executaNavigare(n),
    }));
    return [...navigare, ...comenziExtra];

    function executaNavigare(n: IntrareNav): () => void {
      switch (n.dest.fel) {
        case 'zi':
          return () => void navigate({ to: '/zi/$data', params: { data: ziuaContext } });
        case 'valutar':
          return () => void navigate({ to: '/valutar/$data', params: { data: ziuaContext } });
        case 'raport':
          return () => void navigate({ to: '/raport/$perioada', params: { perioada } });
        default: {
          const to = n.dest.to;
          return () => void navigate({ to });
        }
      }
    }
  }, [navigate, ziuaContext, perioada, comenziExtra]);

  const rezultate = useMemo(() => {
    const nevoie = foldRo(q);
    if (nevoie === '') return comenzi;
    const termeni = nevoie.split(/\s+/).filter(Boolean);
    return comenzi.filter((c) => {
      const subiect = foldRo(`${c.eticheta} ${c.grup} ${c.cuvinte} ${c.detaliu ?? ''}`);
      return termeni.every((t) => subiect.includes(t));
    });
  }, [q, comenzi]);

  useEffect(() => {
    if (deschisa) {
      refAnterior.current = document.activeElement as HTMLElement | null;
      setQ('');
      setSelectat(0);
      // Focusul intra in camp imediat; altfel prima tasta s-ar pierde.
      refInput.current?.focus();
    } else if (refAnterior.current) {
      refAnterior.current.focus();
      refAnterior.current = null;
    }
  }, [deschisa]);

  if (!deschisa) return null;

  const idOptiune = (i: number) => `comanda-${i}`;
  const activ = rezultate[Math.min(selectat, Math.max(0, rezultate.length - 1))];

  function ruleaza(c: Comanda | undefined): void {
    if (!c) return;
    onInchide();
    c.executa();
  }

  return (
    <div
      className="fara-print fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]"
      style={{ background: 'var(--surface-overlay)' }}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onInchide();
      }}
    >
      <div
        ref={refDialog}
        role="dialog"
        aria-modal="true"
        aria-label="Paletă de comenzi"
        className="dialog flex max-h-[70vh] flex-col overflow-hidden"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onInchide();
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectat((i) => (rezultate.length === 0 ? 0 : (i + 1) % rezultate.length));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectat((i) =>
              rezultate.length === 0 ? 0 : (i - 1 + rezultate.length) % rezultate.length,
            );
          } else if (e.key === 'Home') {
            e.preventDefault();
            setSelectat(0);
          } else if (e.key === 'End') {
            e.preventDefault();
            setSelectat(Math.max(0, rezultate.length - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            ruleaza(activ);
          } else if (e.key === 'Tab') {
            // Capcana de focus: dialogul are un singur element focusabil, deci `Tab` nu are unde
            // sa plece. Lista se parcurge cu sagetile (model `combobox`).
            e.preventDefault();
          }
        }}
      >
        <div className="border-b border-hairline p-2">
          <input
            ref={refInput}
            className="input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="lista-comenzi"
            aria-activedescendant={activ ? idOptiune(rezultate.indexOf(activ)) : undefined}
            aria-label="Caută un ecran sau o acțiune"
            placeholder="Caută un ecran sau o acțiune…"
            autoComplete="off"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSelectat(0);
            }}
          />
        </div>

        <ul id="lista-comenzi" role="listbox" aria-label="Rezultate" className="overflow-y-auto p-1">
          {rezultate.length === 0 ? (
            <li role="option" aria-selected="false" aria-disabled="true" className="p-3 text-12 text-ink-2">
              Nimic nu corespunde căutării.
            </li>
          ) : (
            rezultate.map((c, i) => {
              const ales = c === activ;
              return (
                <li
                  key={c.id}
                  id={idOptiune(i)}
                  role="option"
                  aria-selected={ales}
                  className={`flex cursor-pointer items-baseline gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 ${
                    ales ? 'bg-select' : ''
                  }`}
                  onMouseEnter={() => setSelectat(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    ruleaza(c);
                  }}
                >
                  <span className="text-13">{c.eticheta}</span>
                  <span className="text-11 text-ink-3">{c.grup}</span>
                  {c.detaliu ? (
                    <span className="ml-auto truncate text-11 text-ink-3 italic">{c.detaliu}</span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>

        <div className="flex items-center gap-3 border-t border-hairline bg-sticky px-2 py-1 text-11 text-ink-3">
          <span>
            <span className="num">↑↓</span> navighează
          </span>
          <span>
            <span className="num">Enter</span> execută
          </span>
          <span>
            <span className="num">Esc</span> închide
          </span>
        </div>
      </div>
    </div>
  );
}
